import { buildExportCsvFilename, buildExportPdfFilename } from '../../utils/exportPdfFilename';

export const STOCK_QUESTION_TEMPLATE_COLUMNS = [
    { key: 'question', label: 'question' },
    { key: 'tag', label: 'tag' },
    { key: 'response', label: 'response' },
];

export const STOCK_QUESTION_TEMPLATE_ROWS = [
    {
        question: 'Please provide the latest financial statements for Q1 2024',
        tag: 'Financial Reports',
        response: '',
    },
    {
        question: 'Please provide the current shareholder agreement',
        tag: 'Legal Documents',
        response: '',
    },
    {
        question: 'Please provide the organizational structure and headcount',
        tag: 'HR Documents',
        response: '',
    },
    {
        question: 'Please provide details about the IT infrastructure and security measures',
        tag: 'IT Infrastructure',
        response: '',
    },
];

const STOCK_QUESTION_ALIASES = {
    question: ['question', 'title', 'request', 'questions', 'irl question', 'information request'],
    tag: ['tag', 'folder', 'tags'],
    response: ['response', 'answer', 'respond', 'reply'],
    description: ['description', 'context', 'description / context'],
};

export const IRL_EXPORT_COLUMNS = [
    { key: 'id', label: 'Id' },
    { key: 'title', label: 'Title' },
    { key: 'context', label: 'Description' },
    { key: 'workstream', label: 'Workstream' },
    { key: 'status', label: 'Status' },
    { key: 'priority', label: 'Priority' },
    { key: 'due', label: 'Due date' },
    { key: 'owner', label: 'Owner' },
    { key: 'folder', label: 'Folder' },
];

export const OBS_EXPORT_COLUMNS = [
    { key: 'id', label: 'Id' },
    { key: 'title', label: 'Title' },
    { key: 'body', label: 'Description' },
    { key: 'why', label: 'Why it matters' },
    { key: 'workstream', label: 'Workstream' },
    { key: 'status', label: 'Status' },
    { key: 'materiality', label: 'Materiality' },
    { key: 'owner', label: 'Owner' },
];

export const IRL_IMPORT_FIELDS = [
    {
        key: 'id',
        label: 'Id',
        aliases: ['id', 'irl', 'request id'],
        skipAutoMap: true,
        hint: 'Leaving Id unmapped creates new requests. Mapping Id is not written by IRL bulk add; every valid row is created.',
    },
    {
        key: 'title',
        label: 'Title',
        required: true,
        aliases: ['title', 'question', 'questions', 'request', 'irl question', 'information request'],
    },
    { key: 'context', label: 'Description', aliases: ['description', 'context', 'description / context'] },
    {
        key: 'workstream',
        label: 'Workstream',
        aliases: ['workstream', 'workstream name', 'owning workstream'],
        hint: 'A Workstream name that is not in the data room is skipped. Empty cells use the workstream selected above.',
    },
    {
        key: 'status',
        label: 'Status',
        aliases: ['status'],
        importDisabled: true,
        hint: 'Status is included in the export template but is not written on IRL import.',
    },
    { key: 'priority', label: 'Priority', aliases: ['priority'] },
    { key: 'due', label: 'Due date', aliases: ['due', 'due date', 'deadline'], date: true },
    { key: 'owner', label: 'Owner', aliases: ['owner', 'assigned to', 'added by'] },
    { key: 'folder', label: 'Folder', aliases: ['folder', 'destination folder', 'tag', 'tags', 'category', 'categories'] },
    { key: 'response', label: 'Response', aliases: ['response', 'answer', 'respond', 'reply'] },
];

export const OBS_IMPORT_FIELDS = [
    {
        key: 'id',
        label: 'Id',
        aliases: ['id', 'obs', 'observation id'],
        skipAutoMap: true,
        hint: 'Leaving Id unmapped creates new observations; mapping Id updates those observations.',
    },
    { key: 'title', label: 'Title', required: true, aliases: ['title', 'observation', 'question'] },
    { key: 'body', label: 'Description', aliases: ['description', 'body', 'context', 'observation text'] },
    { key: 'why', label: 'Why it matters', aliases: ['why it matters', 'why', 'whyitmatters'] },
    { key: 'workstream', label: 'Workstream', aliases: ['workstream', 'workstream name', 'owning workstream'] },
    { key: 'status', label: 'Status', aliases: ['status'] },
    { key: 'materiality', label: 'Materiality', aliases: ['materiality', 'priority', 'significance', 'impact'] },
    { key: 'owner', label: 'Owner', aliases: ['owner', 'assigned to', 'added by'] },
];

function aliasesFromFields(fields) {
    return Object.fromEntries((fields || []).map(field => [field.key, field.aliases || []]));
}

const IRL_IMPORT_ALIASES = aliasesFromFields(IRL_IMPORT_FIELDS);
const OBS_IMPORT_ALIASES = aliasesFromFields(OBS_IMPORT_FIELDS);

const MONTH_NUMBERS = {
    jan: '01',
    feb: '02',
    mar: '03',
    apr: '04',
    may: '05',
    jun: '06',
    jul: '07',
    aug: '08',
    sep: '09',
    oct: '10',
    nov: '11',
    dec: '12',
};

export function toIsoDueDate(value) {
    if (value == null || value === '') return '';
    const text = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
    const isoPrefix = text.match(/^(\d{4}-\d{2}-\d{2})/);
    if (isoPrefix) return isoPrefix[1];
    const display = text.match(/^(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})$/);
    if (!display) return text;
    const month = MONTH_NUMBERS[display[2].toLowerCase()];
    if (!month) return text;
    return `${display[3]}-${month}-${String(display[1]).padStart(2, '0')}`;
}

function csvEscape(value) {
    const text = value == null ? '' : String(value);
    if (/[",\n\r]/.test(text)) {
        return `"${text.replace(/"/g, '""')}"`;
    }
    return text;
}

export function rowsToCsv(rows, columns) {
    const header = columns.map(column => csvEscape(column.label)).join(',');
    const body = (rows || []).map(row => columns.map(column => csvEscape(row[column.key])).join(','));
    return [header, ...body].join('\n');
}

export function downloadTextFile(filename, text, mime = 'text/csv;charset=utf-8') {
    const blob = new Blob([text], { type: mime });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

export function downloadCsv(rows, columns, filename) {
    downloadTextFile(filename, `\uFEFF${rowsToCsv(rows, columns)}`);
}

function parseCsvLine(line) {
    const cells = [];
    let current = '';
    let quoted = false;
    for (let index = 0; index < line.length; index += 1) {
        const char = line[index];
        if (quoted) {
            if (char === '"') {
                if (line[index + 1] === '"') {
                    current += '"';
                    index += 1;
                } else {
                    quoted = false;
                }
            } else {
                current += char;
            }
        } else if (char === '"') {
            quoted = true;
        } else if (char === ',') {
            cells.push(current);
            current = '';
        } else {
            current += char;
        }
    }
    cells.push(current);
    return cells;
}

function csvRecordLines(text) {
    const raw = String(text || '').replace(/^\uFEFF/, '');
    const lines = [];
    let current = '';
    let quoted = false;
    for (let index = 0; index < raw.length; index += 1) {
        const char = raw[index];
        const next = raw[index + 1];
        if (char === '"') {
            current += char;
            if (quoted && next === '"') {
                current += next;
                index += 1;
            } else {
                quoted = !quoted;
            }
        } else if ((char === '\n' || char === '\r') && !quoted) {
            if (char === '\r' && next === '\n') index += 1;
            if (current.trim()) lines.push(current);
            current = '';
        } else {
            current += char;
        }
    }
    if (current.trim()) lines.push(current);
    return lines;
}

export function parseCsvHeaders(text) {
    const lines = csvRecordLines(text);
    if (!lines.length) return [];
    return parseCsvLine(lines[0])
        .map(header => header.trim())
        .filter(Boolean);
}

export function parseCsvText(text) {
    const lines = csvRecordLines(text);
    if (!lines.length) return [];
    const headers = parseCsvLine(lines[0]).map(header => header.trim());
    return lines.slice(1).map((line, index) => {
        const cells = parseCsvLine(line);
        const row = { _row: index + 2 };
        headers.forEach((header, cellIndex) => {
            row[header] = (cells[cellIndex] || '').trim();
        });
        return row;
    });
}

function normalizeHeader(value) {
    return String(value || '')
        .trim()
        .toLowerCase()
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ');
}

function mappedRow(raw, aliases) {
    const lookup = {};
    Object.keys(raw || {}).forEach(key => {
        if (key === '_row') return;
        lookup[normalizeHeader(key)] = raw[key];
    });
    const mapped = { _row: raw?._row };
    Object.keys(aliases).forEach(field => {
        const match = aliases[field].find(alias => lookup[alias] != null && lookup[alias] !== '');
        if (match) mapped[field] = lookup[match];
    });
    return mapped;
}

export function mapIrlImportRow(raw) {
    const mapped = mappedRow(raw, IRL_IMPORT_ALIASES);
    if (mapped.due) mapped.due = toIsoDueDate(mapped.due);
    return mapped;
}

export function mapObsImportRow(raw) {
    return mappedRow(raw, OBS_IMPORT_ALIASES);
}

function fieldMatchNames(field) {
    return new Set([field.label, ...(field.aliases || [])].map(normalizeHeader));
}

export function autoMapCsvColumns(headers, fields) {
    const mapping = {};
    (headers || []).forEach(header => {
        mapping[header] = '';
    });
    const used = new Set();
    (fields || []).forEach(field => {
        if (field.importDisabled || field.skipAutoMap) return;
        const names = fieldMatchNames(field);
        const match = (headers || []).find(header => !used.has(header) && names.has(normalizeHeader(header)));
        if (!match) return;
        mapping[match] = field.key;
        used.add(match);
    });
    return mapping;
}

export function assignColumnMapping(mapping, header, fieldKey) {
    const next = { ...(mapping || {}), [header]: fieldKey || '' };
    if (fieldKey) {
        Object.keys(next).forEach(other => {
            if (other !== header && next[other] === fieldKey) next[other] = '';
        });
    }
    return next;
}

export function unmappedRequiredFields(mapping, fields) {
    const mapped = new Set(Object.values(mapping || {}).filter(Boolean));
    return (fields || []).filter(field => field.required && !mapped.has(field.key));
}

export function applyColumnMapping(rawRows, mapping, fields = []) {
    const fieldByKey = Object.fromEntries((fields || []).map(field => [field.key, field]));
    return (rawRows || []).map(raw => {
        const mapped = { _row: raw?._row };
        Object.keys(mapping || {}).forEach(header => {
            const fieldKey = mapping[header];
            if (!fieldKey) return;
            const field = fieldByKey[fieldKey];
            if (field?.importDisabled) return;
            let value = raw?.[header];
            if (value == null) value = '';
            value = String(value).trim();
            if (field?.date) value = toIsoDueDate(value);
            mapped[fieldKey] = value;
        });
        return mapped;
    });
}

export function irlUpdatePayload(row, { workstreamMatch, ownerMatch, folderMatch } = {}) {
    const payload = {};
    if (row.title) payload.title = row.title;
    if (row.context) payload.description = row.context;
    if (row.priority) payload.priority = row.priority;
    if (row.due) payload.due_date = toIsoDueDate(row.due);
    if (ownerMatch?.id) payload.owner = ownerMatch.id;
    if (workstreamMatch?.id) payload.workstream_id = workstreamMatch.id;
    if (folderMatch?.id) payload.destination_folder = folderMatch.id;
    return payload;
}

const IRL_BULK_PRIORITIES = {
    high: 'HIGH',
    medium: 'MEDIUM',
    low: 'LOW',
    none: 'NONE',
};

export function irlBulkPriority(value) {
    if (value == null || String(value).trim() === '') return 'NONE';
    const raw = String(value).trim();
    const matched = IRL_BULK_PRIORITIES[raw.toLowerCase()];
    if (matched) return matched;
    const upper = raw.toUpperCase();
    return IRL_BULK_PRIORITIES[upper.toLowerCase()] || '';
}

export function stockQuestionTemplateCsv() {
    return rowsToCsv(STOCK_QUESTION_TEMPLATE_ROWS, STOCK_QUESTION_TEMPLATE_COLUMNS);
}

export function downloadStockQuestionTemplate(project) {
    downloadCsv(
        STOCK_QUESTION_TEMPLATE_ROWS,
        STOCK_QUESTION_TEMPLATE_COLUMNS,
        buildExportCsvFilename({ project, label: 'Information Requests', detail: 'template' }),
    );
}

export const OBS_TEMPLATE_ROWS = [
    {
        id: '',
        title: 'Key person risk',
        body: 'Founder holds all customer contracts.',
        why: 'A single departure would stall the deal.',
        workstream: '',
        status: 'Draft',
        materiality: 'High',
        owner: '',
    },
    {
        id: '',
        title: 'Missing board minutes',
        body: 'Board packs for the last two quarters are not in the data room.',
        why: 'Governance history cannot be verified.',
        workstream: '',
        status: 'Confirmed',
        materiality: 'Medium',
        owner: '',
    },
    {
        id: '',
        title: 'Unclear customer concentration',
        body: 'Top customer share is not broken out in the revenue pack.',
        why: 'Revenue risk may be understated.',
        workstream: '',
        status: 'Draft',
        materiality: 'Low',
        owner: '',
    },
];

export function observationTemplateCsv() {
    return rowsToCsv(OBS_TEMPLATE_ROWS, OBS_EXPORT_COLUMNS);
}

export function downloadObservationTemplate(project) {
    downloadCsv(
        OBS_TEMPLATE_ROWS,
        OBS_EXPORT_COLUMNS,
        buildExportCsvFilename({ project, label: 'Observations', detail: 'template' }),
    );
}

export function mapStockQuestionRow(raw) {
    const mapped = mappedRow(raw, STOCK_QUESTION_ALIASES);
    return {
        ...mapped,
        question: mapped.question || '',
        tag: mapped.tag || '',
        response: mapped.response || '',
        description: mapped.description || '',
    };
}

export function parseStockQuestionCsv(text) {
    return parseCsvText(text).map(row => ({
        ...mapStockQuestionRow(row),
        _row: row?._row,
    }));
}

export function previewStockQuestionRows(rows) {
    return (rows || [])
        .map((item, index) => {
            const question = String(item?.question || item?.title || item?.request || '').trim();
            return {
                ...item,
                id: item?.id || `uploaded-${item?._row ?? index + 2}`,
                question,
                tag: String(item?.tag || item?.folder || ''),
                response: String(item?.response || ''),
            };
        })
        .filter(row => row.question);
}

export function selectedStockQuestionRows(rows, selectedIds) {
    const selected = new Set((selectedIds || []).map(id => String(id)));
    return (rows || []).filter(row => selected.has(String(row.id)));
}

export function stockIrlActorFields({ user, selectedProject, projectUsersAll } = {}) {
    const addedBy = (projectUsersAll || []).find(item => item?.value?.user_id === user?.id)?.value
        ?.group_member_id;
    if (addedBy) return { addedBy };
    const isProjectAdmin =
        selectedProject?.vdr?.vdr_admin === user?.id ||
        selectedProject?.project_admins?.some(admin => admin.user_id === user?.id);
    if (isProjectAdmin) return { addedByOffline: 'Project Admin' };
    return {};
}

export function buildStockIrlBulkAdd(
    rows,
    { workstreams = [], folders = [], vdrId, selectedWorkstream, addedBy, addedByOffline } = {},
) {
    const resolvedWorkstream =
        resolveImportWorkstream(workstreams, selectedWorkstream) || selectedWorkstream || null;
    const items = [];
    const skipped = [];
    (rows || []).forEach(row => {
        const question = String(row?.question || row?.title || row?.request || '').trim();
        if (!question) {
            skipped.push({ row: row?._row, reason: 'missing question' });
            return;
        }
        if (!resolvedWorkstream?.id) {
            skipped.push({ row: row?._row, reason: 'workstream not selected' });
            return;
        }
        const tagText = String(row.tag || row.folder || '').trim();
        const fallbackTag = String(resolvedWorkstream.name || '').trim() || 'General';
        const folderMatch = tagText
            ? matchOption(foldersForWorkstream(folders, resolvedWorkstream.id), tagText)
            : null;
        const item = {
            request: question,
            vdr: vdrId,
            tag: (tagText || fallbackTag).slice(0, 50),
            workstream: resolvedWorkstream.id,
            priorities: 'NONE',
            request_class: 'Custom',
            response: String(row.response || ''),
        };
        const description = String(row.description || row.context || '').trim();
        if (description) item.description = description;
        if (addedBy) item.added_by = addedBy;
        else if (addedByOffline) item.added_by_offline = addedByOffline;
        if (folderMatch?.id) item.destination_folder = folderMatch.id;
        items.push(item);
    });
    return { items, skipped };
}

export function defaultIrlImportWorkstream(workstreams = [], listWorkstream) {
    const listId =
        listWorkstream != null && String(listWorkstream) !== '' && String(listWorkstream) !== 'all'
            ? String(listWorkstream)
            : '';
    if (listId) {
        const match = (workstreams || []).find(option => String(option.id) === listId);
        if (match) return match;
    }
    return (workstreams || [])[0] || null;
}

function resolveImportWorkstream(workstreams, selectedWorkstream) {
    if (selectedWorkstream == null || selectedWorkstream === '') return null;
    if (typeof selectedWorkstream === 'object') {
        if (selectedWorkstream.id != null) {
            return (
                (workstreams || []).find(option => String(option.id) === String(selectedWorkstream.id)) ||
                selectedWorkstream
            );
        }
        return selectedWorkstream;
    }
    return (workstreams || []).find(option => String(option.id) === String(selectedWorkstream)) || null;
}

function foldersForWorkstream(folders, workstreamId) {
    return (folders || []).filter(folder => {
        const folderWorkstream = folder.workstreamId ?? folder.workstream_id ?? folder.workstream;
        if (folderWorkstream == null || folderWorkstream === '') return true;
        return String(folderWorkstream) === String(workstreamId);
    });
}

export function buildIrlBulkAdd(
    rows,
    { workstreams = [], members = [], folders = [], vdrId, selectedWorkstream } = {},
) {
    const selectedExistingWorkstream =
        resolveImportWorkstream(workstreams, selectedWorkstream) || (workstreams || [])[0] || null;
    const items = [];
    const skipped = [];
    (rows || []).forEach(row => {
        if (!row?.title) {
            skipped.push({ row: row?._row, reason: 'missing title' });
            return;
        }
        const namedWorkstream = String(row.workstream || '').trim();
        const workstreamMatch = namedWorkstream ? matchOption(workstreams, namedWorkstream) : null;
        if (namedWorkstream && !workstreamMatch) {
            skipped.push({ row: row._row, reason: `unknown workstream ${namedWorkstream}` });
            return;
        }
        const resolvedWorkstream = workstreamMatch || selectedExistingWorkstream;
        if (!resolvedWorkstream?.id) return;
        const ownerMatch = matchOption(members, row.owner);
        const tagText = String(row.folder || row.tag || '').trim();
        const folderMatch = tagText ? matchOption(foldersForWorkstream(folders, resolvedWorkstream.id), tagText) : null;
        const priority = irlBulkPriority(row.priority);
        if (row.priority && !priority) {
            skipped.push({ row: row._row, reason: `unknown priority ${row.priority}` });
            return;
        }
        const item = {
            request: row.title,
            vdr: vdrId,
            tag: (tagText || String(resolvedWorkstream.name || 'General')).slice(0, 50),
            workstream: resolvedWorkstream.id,
            priorities: priority || 'NONE',
            request_class: 'Custom',
        };
        if (row.context) item.description = row.context;
        if (row.response) item.response = row.response;
        if (row.due) item.due_date = toIsoDueDate(row.due);
        if (ownerMatch?.id) item.added_by = ownerMatch.id;
        if (folderMatch?.id) item.destination_folder = folderMatch.id;
        items.push(item);
    });
    return { items, skipped };
}

export function irlCreatePayload(row, { workstreamMatch, ownerMatch, folderMatch } = {}) {
    const payload = {
        question: row.title,
        workstream_id: workstreamMatch?.id,
    };
    if (row.context) payload.description = row.context;
    if (row.priority) payload.priority = row.priority;
    if (row.due) payload.due_date = toIsoDueDate(row.due);
    if (ownerMatch?.id) payload.owner = ownerMatch.id;
    if (folderMatch?.id) payload.destination_folder = folderMatch.id;
    return payload;
}

export function obsUpdatePayload(row, { workstreamMatch, ownerMatch } = {}) {
    const payload = {};
    if (row.title) payload.title = row.title;
    if (row.body) payload.body = row.body;
    if (row.why) payload.why = row.why;
    if (row.materiality) payload.materiality = row.materiality;
    if (row.status) payload.status = row.status;
    if (ownerMatch?.id) payload.owner = ownerMatch.id;
    if (workstreamMatch?.id) payload.workstream_id = workstreamMatch.id;
    return payload;
}

export function obsCreatePayload(row, matches = {}) {
    return {
        ...obsUpdatePayload(row, matches),
        title: row.title,
        workstream_id: matches.workstreamMatch?.id,
        materiality: row.materiality || 'High',
        status: row.status || 'Confirmed',
    };
}

export function assignedObsWorkstreamId(row) {
    const assigned = String(row?.workstreamId ?? row?.workstream_id ?? '').trim();
    return assigned || '';
}

export function splitObsImportWorkstreams(rows, workstreams = []) {
    const matched = [];
    const needsAssignment = [];
    (rows || []).forEach(row => {
        const title = String(row?.title || '').trim();
        if (!title) return;
        const namedWorkstream = String(row.workstream || '').trim();
        const workstreamMatch = namedWorkstream ? matchOption(workstreams, namedWorkstream) : null;
        if (workstreamMatch?.id) {
            matched.push({
                ...row,
                title,
                workstreamId: String(workstreamMatch.id),
            });
            return;
        }
        needsAssignment.push({
            ...row,
            title,
            originalWorkstream: namedWorkstream,
            workstreamId: '',
        });
    });
    return { matched, needsAssignment };
}

export function applyAssignedWorkstream(rows, selectedIds, workstreamId) {
    const selected = new Set((selectedIds || []).map(id => String(id)));
    const nextId = workstreamId == null ? '' : String(workstreamId);
    return (rows || []).map(row => {
        const key = String(row?._row ?? '');
        if (!key || !selected.has(key)) return row;
        return { ...row, workstreamId: nextId };
    });
}

export function obsImportAssignmentIntro(needCount, matchedCount) {
    const need = Number(needCount) || 0;
    const matched = Number(matchedCount) || 0;
    const needNoun = need === 1 ? 'observation needs' : 'observations need';
    return `${need} ${needNoun} a workstream. ${matched} already match a workstream in the data room.`;
}

export function mergeObsImportRows(matched, assigned) {
    return [...(matched || []), ...(assigned || [])].sort((left, right) => {
        return (Number(left?._row) || 0) - (Number(right?._row) || 0);
    });
}

export function buildObsBulkImport(rows, { workstreams = [], members = [] } = {}) {
    const items = [];
    const skipped = [];
    (rows || []).forEach(row => {
        const title = String(row?.title || '').trim();
        if (!title) {
            skipped.push({ row: row?._row, reason: 'missing title' });
            return;
        }
        const assignedId = assignedObsWorkstreamId(row);
        const namedWorkstream = String(row.workstream || '').trim();
        const workstreamMatch = !assignedId && namedWorkstream ? matchOption(workstreams, namedWorkstream) : null;
        const ownerMatch = matchOption(members, row.owner);
        const item = { title, _row: row._row };
        const rawId = String(row.id || '').trim();
        if (rawId) item.id = rawId;
        if (row.body) item.body = row.body;
        if (row.why) item.why = row.why;
        if (row.status) item.status = row.status;
        if (row.materiality) item.materiality = row.materiality;
        if (assignedId) item.workstream_id = assignedId;
        else if (workstreamMatch?.id) item.workstream_id = workstreamMatch.id;
        if (ownerMatch?.id) item.owner = ownerMatch.id;
        items.push(item);
    });
    return { items, skipped };
}

function sameId(left, right) {
    return String(left || '') === String(right || '');
}

export function findById(rows, value) {
    if (value == null || value === '') return null;
    const needle = String(value).trim();
    return (rows || []).find(row => sameId(row.id, needle) || sameId(row.pk, needle)) || null;
}

export function normalizeMatchName(value) {
    return String(value ?? '')
        .replace(/&amp;/gi, '&')
        .replace(/[\uFF06\uFE60]/g, '&')
        .replace(/&/g, ' and ')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/(^|\s)and(\s|$)/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function optionMatchValues(option, key) {
    const candidate = option?.[key];
    if (candidate == null || candidate === '') return [];
    if (Array.isArray(candidate)) {
        return candidate.filter(item => item != null && item !== '');
    }
    return [candidate];
}

export function matchOption(options, value, keys = ['id', 'name', 'email', 'path', 'short', 'code', 'label']) {
    if (value == null || value === '') return null;
    const needle = normalizeMatchName(value);
    if (!needle) return null;
    return (
        (options || []).find(option =>
            keys.some(key => optionMatchValues(option, key).some(candidate => normalizeMatchName(candidate) === needle)),
        ) || null
    );
}

export function pruneSelection(selectedIds, loadedIds) {
    const allowed = new Set((loadedIds || []).map(id => String(id)));
    return (selectedIds || []).filter(id => allowed.has(String(id)));
}

export function visibleSelectionState(visibleIds, selectedIds) {
    const selected = new Set((selectedIds || []).map(id => String(id)));
    const visible = (visibleIds || []).map(id => String(id));
    const selectedVisible = visible.filter(id => selected.has(id));
    return {
        selectedVisibleCount: selectedVisible.length,
        allVisibleSelected: visible.length > 0 && selectedVisible.length === visible.length,
        someVisibleSelected: selectedVisible.length > 0 && selectedVisible.length < visible.length,
    };
}

export function toggleVisibleSelection(visibleIds, selectedIds) {
    const visible = (visibleIds || []).map(id => String(id));
    const selected = new Set((selectedIds || []).map(id => String(id)));
    const allSelected = visible.length > 0 && visible.every(id => selected.has(id));
    if (allSelected) {
        visible.forEach(id => selected.delete(id));
    } else {
        visible.forEach(id => selected.add(id));
    }
    return Array.from(selected);
}

export function toggleRowSelection(selectedIds, rowId) {
    const current = (selectedIds || []).map(id => String(id));
    const key = String(rowId);
    return current.includes(key) ? current.filter(id => id !== key) : [...current, key];
}

export function sharedRowValue(rows, key) {
    if (!rows?.length) return '';
    const first = String(rows[0]?.[key] ?? '');
    return rows.every(row => String(row?.[key] ?? '') === first) ? first : '';
}

export function filledUpdates(values) {
    const updates = {};
    Object.keys(values || {}).forEach(key => {
        const value = values[key];
        if (value != null && String(value).trim() !== '') updates[key] = value;
    });
    return updates;
}

export function applyBulkDraftPatch(current, patch) {
    return { ...(current || {}), ...(patch || {}) };
}

export function foldersForBulkWorkstream(folders, workstreamId) {
    if (workstreamId == null || String(workstreamId).trim() === '') return folders || [];
    const wanted = String(workstreamId);
    return (folders || []).filter(folder => {
        const folderWorkstream = folder.workstreamId ?? folder.workstream_id ?? folder.workstream;
        if (folderWorkstream == null || folderWorkstream === '') return true;
        return String(folderWorkstream) === wanted;
    });
}

export function bulkDeleteNoun(count, singular, plural) {
    return Number(count) === 1 ? singular : plural;
}

export function bulkDeletePrompt(count, singular, plural) {
    return `Delete ${count} ${bulkDeleteNoun(count, singular, plural)}?`;
}

export function bulkDeleteConfirmPrompt(count, singular, plural) {
    return `This cannot be undone. ${bulkDeletePrompt(count, singular, plural)}`;
}

export function formatBulkDeleteResult({ deleted = 0, failed = [] } = {}, singular, plural) {
    const noun = bulkDeleteNoun(deleted, singular, plural);
    const parts = [`Deleted ${deleted} ${noun}`];
    if (failed.length) {
        const reasons = failed
            .slice(0, 4)
            .map(item => item.id || item.reason)
            .filter(Boolean)
            .join(', ');
        const extra = failed.length > 4 ? ` +${failed.length - 4} more` : '';
        parts.push(`${failed.length} could not be deleted${reasons ? `: ${reasons}${extra}` : extra}`);
    }
    return `${parts.join('. ')}.`;
}

export function formatImportResult({ created = 0, updated = 0, skipped = [] }) {
    const imported = created + updated;
    const parts = [`Imported ${imported}`];
    if (created || updated) {
        const detail = [];
        if (created) detail.push(`${created} created`);
        if (updated) detail.push(`${updated} updated`);
        parts[0] = `${parts[0]} (${detail.join(', ')})`;
    }
    parts.push(`skipped ${skipped.length}`);
    if (skipped.length) {
        const reasons = skipped
            .slice(0, 4)
            .map(item => `row ${item.row}${item.reason ? ` (${item.reason})` : ''}`)
            .join('; ');
        const extra = skipped.length > 4 ? ` +${skipped.length - 4} more` : '';
        parts.push(reasons + extra);
    }
    return parts.filter(Boolean).join('. ');
}

export function listCellValue(row, key) {
    if (key === 'title') return row.title || row.question || '';
    if (key === 'due') return row.due || row.dueDate || '';
    if (key === 'folder') return row.folder || '';
    if (key === 'tag') return row.tag || '';
    if (key === 'priority') return row.priority || '';
    if (key === 'materiality') return row.materiality || row.priority || '';
    if (key === 'status') return row.status || '';
    if (key === 'context') return row.context || row.description || '';
    if (key === 'body') return row.body || '';
    if (key === 'why') return row.why || '';
    return row[key] ?? '';
}

const GENERATED_IRL_PRIORITIES = new Set(['LOW', 'MEDIUM', 'HIGH', 'NONE']);

export function generatedIrlPriority(row) {
    const direct = String(row?.priorities || '').toUpperCase();
    if (GENERATED_IRL_PRIORITIES.has(direct)) return direct;
    const label = String(row?.priority || '').toLowerCase();
    if (label === 'high') return 'HIGH';
    if (label === 'medium') return 'MEDIUM';
    if (label === 'low') return 'LOW';
    return 'NONE';
}

export function buildGeneratedIrlBulkAdd(rows, { vdrId, addedBy, addedByOffline, existingKeys } = {}) {
    const seen = new Set(existingKeys || []);
    const items = [];
    const skipped = [];
    (rows || []).forEach(row => {
        const request = String(row?.request || '').trim();
        if (!request) {
            skipped.push({ reason: 'missing request' });
            return;
        }
        if (row?.workstreamId == null || row.workstreamId === '') {
            skipped.push({ reason: 'missing workstream' });
            return;
        }
        const key = `${row.workstreamId}|${request.toLowerCase()}`;
        if (seen.has(key)) {
            skipped.push({ reason: 'already on the IRL' });
            return;
        }
        seen.add(key);
        const item = {
            request,
            vdr: vdrId,
            tag: String(row.folder || 'General').slice(0, 50),
            workstream: row.workstreamId,
            priorities: generatedIrlPriority(row),
            request_class: 'Custom',
        };
        if (row.folderId != null && row.folderId !== '') item.destination_folder = row.folderId;
        if (addedBy) item.added_by = addedBy;
        else if (addedByOffline) item.added_by_offline = addedByOffline;
        items.push(item);
    });
    return { items, skipped };
}

export function listExportFilenames(label, project) {
    return {
        csv: buildExportCsvFilename({ project, label }),
        pdf: buildExportPdfFilename({ project, label }),
    };
}
